import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { submitInquiry } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import PhoneInput from "../components/PhoneInput";
import SEO from "../components/SEO";
import { useI18n } from "../lib/i18n";
import { useToast } from "../hooks/use-toast";
import { Loader2, CheckCircle2, Film, ArrowLeft } from "lucide-react";

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone_country_code: "+1",
  phone: "",
  tour_date: "",
  hotel: "",
  favorite_shows: "",
  message: "",
};

const DiziTourRequestPage = () => {
  const { lang } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const details = [
        form.tour_date && `Preferred tour date: ${form.tour_date}`,
        form.hotel && `Hotel: ${form.hotel}`,
        form.favorite_shows && `Favourite Turkish dizis: ${form.favorite_shows}`,
        "",
        "Message:",
        form.message || "(none provided)",
      ]
        .filter((line) => line !== false && line !== undefined)
        .join("\n");

      await submitInquiry({
        full_name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone || null,
        phone_country_code: form.phone ? form.phone_country_code : null,
        subject: "Turkish Dizi Tour Request",
        message: details,
        language: lang,
      });
      setDone(true);
      toast({ title: "Request received", description: "We'll be in touch to plan your Dizi tour." });
      setForm(EMPTY);
      setTimeout(() => {
        navigate("/thank-you", {
          state: {
            heading: "Your Dizi tour request is in.",
            message: "We'll put together your Turkish drama filming-location itinerary and follow up within a day.",
          },
        });
      }, 1200);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16">
      <SEO
        title="Plan Your Dizi Tour · Real Istanbul Tours"
        description="Tell us about the Turkish TV series you love, and we'll design a private filming-locations tour around it."
        path="/dizi-tour-request"
      />
      <section className="section-tight bg-[var(--ri-cream)]">
        <div className="container-x max-w-[640px]">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-[var(--ri-muted)] hover:text-[var(--ri-red)] mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <p className="eyebrow mb-3 inline-flex items-center gap-2">
            <Film size={14} /> Turkish Dizi Tours
          </p>
          <h1 className="font-serif-display text-[32px] md:text-[40px] leading-[1.08]">
            Plan your Dizi tour.
          </h1>
          <p className="mt-4 text-[15.5px] leading-[1.75] text-[var(--ri-ink-soft)]">
            Tell us a bit about yourself and the shows you love — we'll put together a private tour of the real
            filming locations around Istanbul, built around your dates and your favourite dizis.
          </p>

          {done ? (
            <div className="mt-10 py-10 text-center rounded-2xl border border-[var(--ri-border)] bg-[#fffdf7]">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="text-green-600" size={28} />
              </div>
              <p className="text-[16px] font-medium">Thanks — your request is in.</p>
              <p className="mt-1 text-[14px] text-[var(--ri-muted)]">We'll reply within a day to start planning.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[13px]">First name</Label>
                  <Input required value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[13px]">Last name</Label>
                  <Input required value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-[13px]">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1" />
              </div>
              <PhoneInput
                label="WhatsApp number"
                code={form.phone_country_code}
                number={form.phone}
                onCodeChange={(v) => update("phone_country_code", v)}
                onNumberChange={(v) => update("phone", v)}
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[13px]">Preferred tour date</Label>
                  <Input type="date" value={form.tour_date} onChange={(e) => update("tour_date", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[13px]">Hotel you're staying at</Label>
                  <Input value={form.hotel} onChange={(e) => update("hotel", e.target.value)} className="mt-1" placeholder="Optional" />
                </div>
              </div>
              <div>
                <Label className="text-[13px]">Which Turkish dizis (TV series) do you love?</Label>
                <Input value={form.favorite_shows} onChange={(e) => update("favorite_shows", e.target.value)} className="mt-1" placeholder="e.g. Kuruluş Osman, Diriliş Ertuğrul..." />
              </div>
              <div>
                <Label className="text-[13px]">Anything else you'd like us to know?</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1" placeholder="Group size, specific scenes or locations you'd love to see, anything at all." />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Send request
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default DiziTourRequestPage;
